import React, { useState, useContext } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  CssBaseline,
  Box,
  useTheme,
  useMediaQuery,
  Avatar
} from '@mui/material';
import Menu from '../components/Menu';
import { useConfigEmpresa } from '../context/ConfigEmpresaContext';

import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminIcon,
  Engineering as EngineeringIcon,
  MiscellaneousServices as ServicesIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  ListAlt as ListAltIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  ViewKanban as ViewKanbanIcon,
  MyLocation as MyLocationIcon,
  Storefront as StorefrontIcon,
  Inventory as InventoryIcon,
  DynamicForm as DynamicFormIcon,
  History as HistoryIcon,
  CardMembership as PlanIcon
} from '@mui/icons-material';
import BannerUpgrade from '../components/suscripcion/BannerUpgrade';

const drawerWidth = 240;
const mobileDrawerWidth = 280;

function HomePage() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { config } = useConfigEmpresa();
  const { user } = useContext(AuthContext);
  const nivel = user?.nivel || 99;

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  // Cierra el drawer automáticamente en móvil después de navegar
  const handleMenuClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  // Construir opciones del menú filtradas por nivel de usuario
  // Nivel 1: Super Admin (ve todo + admin)
  // Nivel 2: Admin Empresa (ve todo de /home)
  // Nivel 3: Coordinador (solo órdenes, clientes, tracking)
  // Nivel 4+: Bloqueado en login (solo APP móvil)
  const buildMenuOptions = () => {
    const menu = [
      {
        text: 'Home',
        icon: <HomeIcon />,
        path: '/home',
        onClick: handleMenuClick
      },
      // Mi Plan — visible para admin (nivel 1, 2)
      ...(nivel <= 2 ? [{
        text: 'Mi Plan',
        icon: <PlanIcon />,
        path: '/home/suscripcion',
        onClick: handleMenuClick
      }] : []),
    ];

    // Tablero Kanban — nivel 1, 2
    if (nivel <= 2) {
      menu.push({
        text: 'Tablero Kanban',
        icon: <ViewKanbanIcon />,
        path: '/home/tablero',
        onClick: handleMenuClick
      });
    }

    // Ubica tu Personal (GIS) — nivel 1, 2, 3
    if (config?.soporte_gis === true && nivel <= 3) {
      menu.push({
        text: 'Ubica tu Personal',
        icon: <MyLocationIcon />,
        path: '/home/tracking',
        onClick: handleMenuClick
      });
    }

    // Gestión OS — todos los niveles pero con opciones filtradas
    const gestionSubOptions = [
      {
        text: 'Ordenes de Servicio',
        icon: <AssignmentIcon />,
        path: '/home/ordenes',
        onClick: handleMenuClick
      },
      {
        text: 'Clientes',
        icon: <PeopleIcon />,
        path: '/home/clientes',
        onClick: handleMenuClick
      },
    ];

    // Aprobar, GPS, Liquidar, Pagos — solo nivel 1, 2
    if (nivel <= 2) {
      gestionSubOptions.push(
        {
          text: 'Aprobar Servicios',
          icon: <CheckCircleIcon />,
          path: '/home/aprobar',
          onClick: handleMenuClick
        },
        {
          text: 'Liquidar',
          icon: <ReceiptIcon />,
          path: '/home/liquidacion',
          onClick: handleMenuClick
        },
        {
          text: 'Pagos',
          icon: <PaymentIcon />,
          path: '/home/pagos',
          onClick: handleMenuClick
        },
      );
    }

    menu.push({
      text: 'Gestión OS',
      icon: <ListAltIcon />,
      subOptions: gestionSubOptions,
    });

    // Empresa — solo nivel 1, 2
    if (nivel <= 2) {
      menu.push({
        text: 'Empresa',
        icon: <BusinessIcon />,
        subOptions: [
          {
            text: 'Datos Empresa',
            icon: <StorefrontIcon />,
            path: '/home/miempresa',
            onClick: handleMenuClick
          },
          {
            text: 'Locales',
            icon: <StoreIcon />,
            path: '/home/locales',
            onClick: handleMenuClick
          },
          {
            text: 'Trabajadores',
            icon: <EngineeringIcon />,
            path: '/home/trabajadores',
            onClick: handleMenuClick
          },
          {
            text: 'Catálogo Servicios',
            icon: <InventoryIcon />,
            path: '/home/servicios',
            onClick: handleMenuClick
          },
        ],
      });
    }

    // Mantenimiento — solo nivel 1, 2
    if (nivel <= 2) {
      menu.push({
        text: 'Mantenimiento',
        icon: <SettingsIcon />,
        subOptions: [
          {
            text: 'Períodos',
            icon: <ScheduleIcon />,
            path: '/home/periodos',
            onClick: handleMenuClick
          },
          {
            text: 'Integridad',
            icon: <ServicesIcon />,
            path: '/home/admin-tools',
            onClick: handleMenuClick
          },
          {
            text: 'Usuarios',
            icon: <AdminIcon />,
            path: '/home/usuarios',
            onClick: handleMenuClick
          },
        ],
      });
    }

    return menu;
  };

  const menuOptions = buildMenuOptions();

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* AppBar responsivo */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #1A237E 0%, #283593 50%, #3949AB 100%)',
          boxShadow: '0 4px 20px rgba(26, 35, 126, 0.15)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar sx={{ 
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 2, sm: 3 }
        }}>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ 
              marginRight: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>

          {/* Logo y título responsivo */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1,
            gap: 2
          }}>
            {/* Logo - Coloca tu PNG en public/assets/logo.png */}
            <Avatar
              src="/assets/logo.png" // Ruta donde debes colocar tu logo
              alt="TRACKEA Logo"
              sx={{
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 },
                backgroundColor: 'rgba(79, 195, 247, 0.2)',
                // Fallback si no encuentra la imagen
                '& .MuiAvatar-img': {
                  objectFit: 'contain'
                }
              }}
            >
              T
            </Avatar>

            <Box>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                noWrap
                sx={{ 
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #ffffff, #E3F2FD)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.2
                }}
              >
                TRACKEA
              </Typography>
              {!isMobile && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    opacity: 0.8,
                    display: 'block',
                    fontSize: '0.7rem',
                    letterSpacing: 1
                  }}
                >
                  Panel de Control
                </Typography>
              )}
            </Box>
          </Box>

          {/* Indicador de responsive */}
          {!isMobile && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              opacity: 0.7
            }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#4CAF50',
                animation: 'pulse 2s infinite'
              }} />
              <Typography variant="caption">
                En línea
              </Typography>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Menu lateral responsivo */}
      <Menu 
        menuOptions={menuOptions} 
        open={open} 
        onToggle={handleDrawerToggle}
        isMobile={isMobile}
        drawerWidth={isMobile ? mobileDrawerWidth : drawerWidth}
      />

      {/* Contenido principal responsivo */}
      

<Box
  component="main"
  sx={{ 
    flexGrow: 1, 
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
    // ✅ ELIMINAR width y marginLeft que causan el problema
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // ✅ Solo padding responsive
    p: 0, // Sin padding en el contenedor principal
    pt: {
      xs: 8, // Espacio para AppBar en móvil
      sm: 10 // Espacio para AppBar en desktop
    }
  }}
>
  {/* Overlay para móvil cuando el drawer está abierto */}
  {isMobile && open && (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: theme.zIndex.drawer - 1,
      }}
      onClick={handleDrawerToggle}
    />
  )}

  {/* ✅ NUEVO: Contenedor que ocupa todo el ancho */}
  <Box sx={{
    width: '100%', // ✅ Cambiado de maxWidth a width
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 0, // ✅ Sin border radius para ocupar todo el espacio
    minHeight: 'calc(100vh - 80px)',
    boxShadow: 'none', // ✅ Sin sombra
    backdropFilter: 'blur(10px)',
    overflow: 'hidden'
  }}>
    <BannerUpgrade />
    <Outlet />
  </Box>
</Box>



      {/* Animación de pulso para el indicador online */}
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
          }
          
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
          }
          
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
          }
        }
      `}</style>
    </Box>
  );
}

export default HomePage;